import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/axios-instance";

/**
 * Unified access model for the suite (Fase B/D):
 * - org level  = CP entitlements (incl. the area modules HERIKA_ADMIN/HERIKA_DEV)
 * - user level = Keycloak client roles on each module's client
 * The BE merges both in `GET /api/v1/me/access`; an area/module is usable only
 * when BOTH org_enabled and user_has_access are true.
 *
 * AVES_SYNC M0 additions (all optional, absent on older BEs):
 * - areas.admin.sections: per-section visibility, already the AND of the org
 *   `admin.section.*` flags and the user `admin:section:*` whitelist. Absent
 *   or empty = every section allowed (legacy fail-open).
 * - module.features: gated-feature values merged with registry defaults.
 * - module.permissions: user's roles on the module's Keycloak client.
 * - module.category: "sync" for Sync Center modules (code *_SYNC).
 */
export interface AreaAccess {
  org_enabled: boolean;
  user_has_access: boolean;
  sections?: Record<string, boolean> | null;
}

export interface ModuleAccessEntry {
  code: string;
  name: string;
  type: string;
  org_enabled: boolean;
  user_has_access: boolean;
  target_url?: string | null;
  features?: Record<string, boolean> | null;
  permissions?: string[] | null;
  category?: string | null;
}

export interface MyAccess {
  has_booking_engine: boolean;
  areas: Record<string, AreaAccess>;
  modules: ModuleAccessEntry[];
}

const EMPTY: MyAccess = { has_booking_engine: true, areas: {}, modules: [] };

export function useMyAccess(): {
  access: MyAccess;
  isLoading: boolean;
  hasBookingEngine: boolean;
  canArea: (area: "admin" | "dev" | "crm") => boolean;
  canSection: (area: "admin" | "dev" | "crm", section: string) => boolean;
  can: (moduleCode: string, permission: string) => boolean;
  moduleByCode: (code: string) => ModuleAccessEntry | undefined;
} {
  const { data, isLoading } = useQuery({
    queryKey: ["me", "access"],
    queryFn: async () => {
      const { data } = await apiClient.get<MyAccess>("/api/v1/me/access");
      return data;
    },
    staleTime: 60_000,
    retry: 1,
  });

  const access = data ?? EMPTY;
  const moduleByCode = (code: string) =>
    access.modules.find((m) => m.code.toUpperCase() === code.toUpperCase());
  return {
    access,
    isLoading,
    // While loading default to true so we don't flash-disable the whole home
    // for orgs that DO have the booking engine (the common case).
    hasBookingEngine: data ? data.has_booking_engine : true,
    canArea: (area) => {
      const a = access.areas[area];
      return Boolean(a && a.org_enabled && a.user_has_access);
    },
    // Fail-open on absent/empty sections (old BE or non-admin areas): the
    // BE already resolved org AND user, the FE never computes policy.
    canSection: (area, section) => {
      const sections = access.areas[area]?.sections;
      if (!sections || Object.keys(sections).length === 0) return true;
      return sections[section] === true;
    },
    // Fail-closed on absent permissions: action buttons for the new sync
    // UIs stay hidden against an old BE.
    can: (moduleCode, permission) =>
      Boolean(moduleByCode(moduleCode)?.permissions?.includes(permission)),
    moduleByCode,
  };
}
